/**
 * Sign-in by name, for people who have no email worth using: the office creates the
 * login, or resets a forgotten password, and reads the temporary password out here.
 *
 * Two steps in one dialog. Ask (what is about to happen and, for a person whose name
 * gives no usable login name, a field to type one), then the result: the login name and
 * the password, shown once. The password lives only in this component's state; closing
 * the dialog throws it away, which is the point.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Field } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type IssuedLogin, type User } from '@/lib/api';
import { fullName } from '@/lib/format';

import { copyText, isValidLoginName, needsLoginName, tidyLoginName, typedAsName } from './nameLogin';

type Person = Pick<User, 'id' | 'firstName' | 'lastName' | 'email' | 'loginName'>;

export function NameLoginDialog({
  person,
  mode,
  open,
  onClose,
  intro,
}: {
  person: Person;
  mode: 'create' | 'reset';
  open: boolean;
  onClose: () => void;
  /** Replaces the usual explanation, e.g. right after adding someone with no email. */
  intro?: string;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState<IssuedLogin | null>(null);

  const issue = useMutation({
    mutationFn: () =>
      mode === 'create'
        ? api.createNameLogin(person.id, asking ? tidyLoginName(typed) : undefined)
        : api.resetPassword(person.id),
    onSuccess: async (r) => {
      setResult(r);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', person.id] });
    },
    onError: (e) => {
      if (needsLoginName(e)) setAsking(true);
    },
  });

  // Every opening starts clean: no password, no half-typed name from the last person.
  const { reset: resetIssue } = issue;
  useEffect(() => {
    if (open) {
      setAsking(false);
      setTyped('');
      setResult(null);
      resetIssue();
    }
  }, [open, person.id, resetIssue]);

  const name = fullName(person);
  const typedName = tidyLoginName(typed);
  const nameOk = !asking || isValidLoginName(typedName);

  const error =
    issue.error && !needsLoginName(issue.error)
      ? issue.error instanceof ApiError
        ? issue.error.message
        : t('common.saveError')
      : null;

  const title = result
    ? t('access.nameLogin.resultTitle', { name })
    : t(mode === 'create' ? 'access.nameLogin.createTitle' : 'access.nameLogin.resetTitle', { name });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={title}
      footer={
        result ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('access.nameLogin.done')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={issue.isPending || !nameOk || (asking && typedName === '')}
              onClick={() => issue.mutate()}
            >
              {issue.isPending ? t('common.saving') : t('access.nameLogin.generate')}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              {intro ? t('access.nameLogin.notNow') : t('common.cancel')}
            </button>
          </>
        )
      }
    >
      {result ? (
        <Result person={person} result={result} />
      ) : (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed">
            {intro ?? t(mode === 'create' ? 'access.nameLogin.createIntro' : 'access.nameLogin.resetIntro')}
          </p>
          {mode === 'create' && person.email ? (
            <p className="rounded-lg bg-surface-alt px-3 py-2.5 text-xs leading-relaxed text-muted">
              {t('access.nameLogin.leavesEmail', { email: person.email })}
            </p>
          ) : null}

          {asking ? (
            <div className="space-y-2">
              <p className="rounded-lg bg-surface-alt px-3 py-2.5 text-sm leading-relaxed" role="status">
                {t('access.nameLogin.needName')}
              </p>
              <Field label={t('access.nameLogin.loginName')}>
                <input
                  className="input font-mono"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="mario.rossi"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </Field>
              {typed.trim() !== '' && !nameOk ? (
                <p className="text-xs text-danger">{t('access.nameLogin.nameInvalid')}</p>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function Result({ person, result }: { person: Person; result: IssuedLogin }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState<'password' | 'instruction' | 'failed' | null>(null);

  const loginName = result.loginName ?? person.loginName;
  const plain = typedAsName(person, loginName);
  const instruction = plain
    ? t('access.nameLogin.instruction', {
        first: person.firstName,
        last: person.lastName,
        password: result.temporaryPassword,
      })
    : t('access.nameLogin.instructionLogin', { loginName: loginName ?? '', password: result.temporaryPassword });

  const copy = async (what: 'password' | 'instruction') => {
    const ok = await copyText(what === 'password' ? result.temporaryPassword : instruction);
    setCopied(ok ? what : 'failed');
    setTimeout(() => setCopied(null), 4000);
  };

  return (
    <div className="space-y-4">
      {loginName ? (
        <div>
          <div className="mb-1 text-xs font-semibold text-muted">{t('access.nameLogin.loginName')}</div>
          <div className="font-mono text-sm font-semibold">{loginName}</div>
          <p className="mt-1 text-xs leading-relaxed text-muted">
            {plain
              ? t('access.nameLogin.typedHint', { first: person.firstName, last: person.lastName })
              : t('access.nameLogin.differentName', { loginName })}
          </p>
        </div>
      ) : null}

      <div>
        <div className="mb-1 text-xs font-semibold text-muted">{t('access.nameLogin.password')}</div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            aria-label={t('access.nameLogin.password')}
            className="input min-w-0 flex-1 select-all text-center font-mono text-2xl font-bold tracking-wider"
            value={result.temporaryPassword}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="btn-secondary" onClick={() => void copy('password')}>
            {copied === 'password' ? t('access.nameLogin.copied') : t('access.nameLogin.copy')}
          </button>
        </div>
        {copied === 'failed' ? (
          <p className="mt-1 text-xs text-danger" role="alert">
            {t('access.nameLogin.copyFailed')}
          </p>
        ) : null}
      </div>

      <p className="rounded-lg border border-warning px-3 py-2.5 text-sm leading-relaxed" role="alert">
        {t('access.nameLogin.onceWarning')}
      </p>

      <div>
        <div className="mb-1 text-xs font-semibold text-muted">{t('access.nameLogin.instructionLabel')}</div>
        <p className="select-all rounded-lg bg-surface-alt px-3 py-2.5 text-sm leading-relaxed">{instruction}</p>
        <button type="button" className="btn-secondary mt-2" onClick={() => void copy('instruction')}>
          {copied === 'instruction' ? t('access.nameLogin.copied') : t('access.nameLogin.copyInstruction')}
        </button>
      </div>
    </div>
  );
}
